import { Trans, useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { useUpdateArticle } from '@/models/article';
import { useUpdateArticleExchange } from '@/models/articleExchange';
import { useSizesOptionsSorted } from '@/models/sizeProduct';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { type I18nWording } from '@/services/i18n';

const Size = ({ isReplacementProduct = false }: { isReplacementProduct?: boolean }) => {
  const { t } = useLingui();
  const { article, view, errors } = useArticleContext();
  const { sizesOptions, sizeOptionLabel, sizeOptionValue } = useSizesOptionsSorted({
    article,
    isReplacementProduct,
  });

  const { mutate: updateArticleExchange } = useUpdateArticleExchange(article.id);
  const { mutate: updateArticle } = useUpdateArticle(
    {
      articleId: article.id,
      requestId: article.requestId,
    },
    {
      optimistic: true,
    }
  );

  const size = isReplacementProduct
    ? article.snapshot.articleExchange?.replacementSize
    : article.size;

  const selectedSizeOption =
    sizesOptions.find(
      (option) =>
        size !== null && size !== undefined && sizeOptionValue(option) === sizeOptionValue(size)
    ) ?? null;

  const isUniqueArticleWithSingleSize = !!article.uniqueArticle && sizesOptions.length === 1;

  const isMandatory =
    isReplacementProduct || view.details.size.required || isUniqueArticleWithSingleSize;

  const hasError = isReplacementProduct
    ? errors.services?.exchange?.missingReplacementProductSize
    : errors?.details?.missingSize;

  const error = hasError
    ? t({ id: 'article.form.size.error', message: 'Please select a size' })
    : undefined;

  const handleChange = (sizeOption: I18nWording | null) => {
    if (isReplacementProduct) {
      updateArticleExchange({ replacementSize: sizeOption });
    } else {
      updateArticle({ data: { size: sizeOption } });
    }
  };

  const isDisabled = sizesOptions.length === 0 || isUniqueArticleWithSingleSize;

  return (
    <InputSelect
      variant="select"
      label={
        <>
          <Trans id="article.form.size.label">Size</Trans>
          {!isMandatory && !isDisabled && <OptionalLabel />}
        </>
      }
      placeholder={
        sizesOptions.length > 0
          ? t({ id: 'article.form.size.placeholder', message: 'Select a size' })
          : t({ id: '_general.na', message: 'N/A' })
      }
      isDisabled={isDisabled}
      value={selectedSizeOption}
      options={sizesOptions}
      getOptionValue={sizeOptionValue}
      getOptionLabel={sizeOptionLabel}
      onChange={(sizeOption) => {
        handleChange(sizeOption);
      }}
      isClearable
      style={{ flex: 1 }}
      error={error}
    />
  );
};

export default Size;
